import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, RotateCcw, SwitchCamera, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCamera } from '@/hooks/useCamera';
import { useClothingAnalysis } from '@/hooks/useClothingAnalysis';
import { cn } from '@/lib/utils';

interface CameraCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File, analysis?: any) => void;
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  isOpen,
  onClose,
  onCapture,
}) => {
  const {
    isOpen: cameraActive,
    error,
    facingMode,
    videoRef,
    canvasRef,
    openCamera,
    closeCamera,
    capturePhoto,
    flipCamera,
    hasMultipleCameras,
  } = useCamera({ facingMode: 'environment' });

  const { analyzing, analysis, analyzeImage, reset } = useClothingAnalysis();
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [capturedPreview, setCapturedPreview] = useState<string | null>(null);
  const [step, setStep] = useState<'camera' | 'preview' | 'analyzing'>('camera');

  useEffect(() => {
    if (isOpen && !cameraActive) {
      openCamera();
    }
    return () => {
      if (!isOpen) {
        closeCamera();
        reset();
        setCapturedFile(null);
        setCapturedPreview(null);
        setStep('camera');
      }
    };
  }, [isOpen]);

  const handleCapture = () => {
    const file = capturePhoto();
    if (file) {
      setCapturedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPreview(reader.result as string);
        setStep('preview');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRetake = () => {
    setCapturedFile(null);
    setCapturedPreview(null);
    reset();
    setStep('camera');
  };

  const handleAnalyze = async () => {
    if (!capturedFile) return;
    setStep('analyzing');
    await analyzeImage(capturedFile);
  };

  const handleConfirm = () => {
    if (capturedFile) {
      onCapture(capturedFile, analysis);
      handleClose();
    }
  };

  const handleClose = () => {
    closeCamera();
    reset();
    setCapturedFile(null);
    setCapturedPreview(null);
    setStep('camera');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black"
      >
        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Camera View */}
        {step === 'camera' && (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={cn(
                'absolute inset-0 w-full h-full object-cover',
                facingMode === 'user' && 'scale-x-[-1]'
              )}
            />

            {/* Overlay UI */}
            <div className="absolute inset-0 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
                <button onClick={handleClose} className="p-2 text-white">
                  <X size={24} />
                </button>
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-aura-gold" />
                  <span className="text-white text-sm font-medium">AI Capture</span>
                </div>
                <div className="w-10" />
              </div>

              {/* Center frame guide */}
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-sm aspect-[3/4] border-2 border-white/30 rounded-3xl relative">
                  <div className="absolute -top-1 -left-1 w-8 h-8 border-t-2 border-l-2 border-aura-gold rounded-tl-xl" />
                  <div className="absolute -top-1 -right-1 w-8 h-8 border-t-2 border-r-2 border-aura-gold rounded-tr-xl" />
                  <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-2 border-l-2 border-aura-gold rounded-bl-xl" />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-2 border-r-2 border-aura-gold rounded-br-xl" />
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-white/60 text-xs text-center px-4">
                      Position clothing item within frame
                    </p>
                  </div>
                </div>
              </div>

              {/* Bottom controls */}
              <div className="p-6 pb-12 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center justify-center gap-8">
                  {hasMultipleCameras && (
                    <button
                      onClick={flipCamera}
                      className="p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                    >
                      <SwitchCamera size={24} />
                    </button>
                  )}
                  
                  <button
                    onClick={handleCapture}
                    className="w-20 h-20 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform active:scale-95"
                  >
                    <div className="w-16 h-16 rounded-full border-4 border-black/20 flex items-center justify-center">
                      <Camera size={28} className="text-black/80" />
                    </div>
                  </button>

                  <div className="w-12" />
                </div>
              </div>
            </div>

            {/* Error display */}
            {error && (
              <div className="absolute bottom-32 left-4 right-4 p-4 bg-destructive/90 rounded-xl text-white text-center text-sm">
                {error}
              </div>
            )}
          </>
        )}

        {/* Preview Step */}
        {(step === 'preview' || step === 'analyzing') && capturedPreview && (
          <div className="absolute inset-0 flex flex-col bg-background">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <button onClick={handleRetake} className="p-2 text-muted-foreground">
                <RotateCcw size={20} />
              </button>
              <span className="text-sm font-medium">
                {step === 'analyzing' ? 'Analyzing...' : 'Preview'}
              </span>
              <button onClick={handleClose} className="p-2 text-muted-foreground">
                <X size={20} />
              </button>
            </div>

            {/* Image preview */}
            <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
              <div className="relative max-w-sm w-full">
                <img
                  src={capturedPreview}
                  alt="Captured"
                  className="w-full rounded-2xl shadow-2xl"
                />
                
                {step === 'analyzing' && (
                  <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-2xl flex flex-col items-center justify-center">
                    <div className="relative">
                      <div className="w-16 h-16 border-2 border-aura-gold/30 rounded-full animate-pulse" />
                      <div className="absolute inset-0 w-16 h-16 border-2 border-aura-gold border-t-transparent rounded-full animate-spin" />
                      <Sparkles className="absolute inset-0 m-auto text-aura-gold" size={24} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">AI Analyzing...</p>
                  </div>
                )}

                {analysis && step !== 'analyzing' && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-2">
                    <Check size={16} />
                  </div>
                )}
              </div>
            </div>

            {/* Analysis results */}
            {analysis && step !== 'analyzing' && (
              <div className="px-4 pb-4">
                <div className="p-4 bg-card rounded-xl border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-aura-gold" />
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">AI Detected</span>
                  </div>
                  <p className="font-medium text-foreground">{analysis.suggestedName}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-secondary rounded-full">{analysis.type}</span>
                    <span className="text-xs px-2 py-1 bg-secondary rounded-full">{analysis.color}</span>
                    <span className="text-xs px-2 py-1 bg-secondary rounded-full">{analysis.occasion}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="p-4 pb-8 flex gap-3">
              {step === 'preview' && !analysis && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleRetake}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Retake
                  </Button>
                  <Button
                    onClick={handleAnalyze}
                    className="flex-1 h-12 rounded-xl btn-aura"
                  >
                    <Sparkles size={16} className="mr-2" />
                    Analyze
                  </Button>
                </>
              )}

              {analysis && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleRetake}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Retake
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    className="flex-1 h-12 rounded-xl btn-aura"
                  >
                    <Check size={16} className="mr-2" />
                    Add to Archive
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
